/**
 * i18n онбординга раздела «Маршрутизация обращений» (DEV-352).
 *
 * Вынесено отдельным модулем и подмешивается в основные локали через deep-merge
 * в ./index.ts — чтобы не раздувать монолитные ru/en/vn расширенным блоком.
 * Ключи кладутся в namespace routing.* (и common.actions.gotIt). Значения
 * переопределяют одноимённые ключи базовых локалей (напр. fields.primaryValueHint
 * со строки на объект, fields.requestTypePlaceholder).
 *
 * ВАЖНО: не использовать литеральные фигурные скобки `{`/`}` в строках —
 * vue-i18n трактует их как интерполяцию и падает с SyntaxError при рендере.
 * Конкретный JSON-пример для condition держим в JS-константе CONDITION_EXAMPLE
 * (routingOptions.ts) и отдаём как placeholder, минуя i18n-компилятор.
 */

type LocaleOverride = Record<string, unknown>

export const routingOnboardingMessages: Record<'ru' | 'en' | 'vn', LocaleOverride> = {
  ru: {
    common: { actions: { gotIt: 'Понятно' } },
    routing: {
      requestTypeOptions: {
        commission_question: 'Вопрос по вознаграждению',
        meeting_request: 'Запрос на встречу',
        strategy_question: 'Вопрос по стратегии',
        other: 'Другой вопрос',
      },
      roleOptions: {
        HOS: 'Руководитель отдела продаж (РОП)',
        ADMIN: 'Администратор',
        MANAGER: 'Менеджер',
        AGENT: 'Агент',
        MARKETER: 'Маркетолог',
      },
      emptyState: {
        title: 'Пока нет ни одного правила маршрутизации',
        description:
          'Правило определяет, кому направить обращение агента по его типу и через сколько минут эскалировать, если никто не ответил. Создайте первое правило, чтобы обращения попадали нужным людям автоматически.',
        example: 'Пример: «Вопрос по вознаграждению» → роль РОП, эскалация через 60 минут.',
      },
      howItWorks: {
        title: 'Как это работает',
        intro:
          'Когда агент создаёт обращение, система подбирает правило по типу обращения и направляет его выбранному ответственному. Если ответа нет за время SLA — обращение эскалируется на резервную роль.',
        examplesLabel: 'Типы обращений:',
        items: {
          requestType: {
            term: 'Тип обращения',
            text: 'категория запроса агента: вопрос по вознаграждению, запрос на встречу, вопрос по стратегии или другой.',
          },
          primary: {
            term: 'Кому направлять',
            text: 'основной ответственный — конкретная роль, конкретный пользователь или менеджер сделки.',
          },
          fallback: {
            term: 'Резервная роль',
            text: 'кому уйдёт обращение, если основной ответственный не отреагировал за время SLA. По умолчанию — РОП.',
          },
          sla: {
            term: 'SLA, минут',
            text: 'сколько минут даётся на ответ до эскалации на резервную роль.',
          },
          condition: {
            term: 'Условие',
            text: 'необязательное JSON-условие, при котором правило срабатывает (например, только для дорогих сделок).',
          },
        },
      },
      welcome: {
        title: 'Маршрутизация обращений — с чего начать',
        body: 'Здесь вы настраиваете, кому автоматически направляются обращения агентов и через сколько минут их эскалировать. Каждое правило связывает тип обращения с ответственным и таймером SLA. Ниже — блок «Как это работает» с разбором всех полей.',
      },
      tooltips: {
        requestType: 'Категория обращения. Выберите из списка известных типов.',
        primaryKind: 'Способ выбора ответственного: роль, конкретный пользователь или менеджер сделки.',
        primaryValue: 'Зависит от способа выбора: ключ роли, ID пользователя или пусто для менеджера сделки.',
        fallbackRoleKey: 'Роль, на которую уйдёт обращение при просрочке SLA.',
        slaMinutes: 'Время до эскалации, в минутах.',
        condition: 'Необязательное JSON-условие срабатывания правила.',
      },
      fields: {
        requestTypePlaceholder: 'Выберите тип обращения',
        fallbackRoleKeyHint: 'Роль для эскалации при просрочке SLA (по умолчанию РОП).',
        conditionHint:
          'Необязательно. JSON-условие, при котором срабатывает правило — например, ограничение по минимальной сумме сделки.',
        conditionPlaceholder: 'JSON-условие (необязательно)',
        primaryValueHint: {
          role: 'Ключ роли, например HOS',
          user: 'ID пользователя, например 42',
          dealManager: 'Не требуется — будет выбран менеджер сделки',
        },
      },
    },
  },
  en: {
    common: { actions: { gotIt: 'Got it' } },
    routing: {
      requestTypeOptions: {
        commission_question: 'Commission question',
        meeting_request: 'Meeting request',
        strategy_question: 'Strategy question',
        other: 'Other question',
      },
      roleOptions: {
        HOS: 'Head of Sales (HOS)',
        ADMIN: 'Administrator',
        MANAGER: 'Manager',
        AGENT: 'Agent',
        MARKETER: 'Marketer',
      },
      emptyState: {
        title: 'No routing rules yet',
        description:
          'A rule defines who an agent inquiry goes to (by its type) and after how many minutes to escalate if no one responds. Create your first rule so inquiries reach the right people automatically.',
        example: 'Example: “Commission question” → Head of Sales role, escalate after 60 minutes.',
      },
      howItWorks: {
        title: 'How it works',
        intro:
          'When an agent submits an inquiry, the system picks a rule by inquiry type and routes it to the chosen assignee. If there is no response within the SLA, the inquiry escalates to the fallback role.',
        examplesLabel: 'Inquiry types:',
        items: {
          requestType: {
            term: 'Inquiry type',
            text: 'the agent request category: commission question, meeting request, strategy question or other.',
          },
          primary: {
            term: 'Route to',
            text: 'the primary assignee — a specific role, a specific user, or the deal manager.',
          },
          fallback: {
            term: 'Fallback role',
            text: 'who receives the inquiry if the primary assignee does not respond within the SLA. Defaults to Head of Sales.',
          },
          sla: {
            term: 'SLA, minutes',
            text: 'how many minutes are allowed to respond before escalation to the fallback role.',
          },
          condition: {
            term: 'Condition',
            text: 'an optional JSON condition under which the rule fires (e.g. only for high-value deals).',
          },
        },
      },
      welcome: {
        title: 'Inquiry routing — where to start',
        body: 'Here you configure who agent inquiries are routed to automatically and after how many minutes to escalate them. Each rule links an inquiry type to an assignee and an SLA timer. See the “How it works” panel below for a breakdown of every field.',
      },
      tooltips: {
        requestType: 'The inquiry category. Pick from the list of known types.',
        primaryKind: 'How the assignee is chosen: a role, a specific user, or the deal manager.',
        primaryValue: 'Depends on the choice: a role key, a user ID, or empty for the deal manager.',
        fallbackRoleKey: 'The role the inquiry escalates to when the SLA is missed.',
        slaMinutes: 'Time until escalation, in minutes.',
        condition: 'An optional JSON condition for when the rule fires.',
      },
      fields: {
        requestTypePlaceholder: 'Select an inquiry type',
        fallbackRoleKeyHint: 'Role for escalation when the SLA is missed (defaults to Head of Sales).',
        conditionHint:
          'Optional. A JSON condition under which the rule fires — e.g. a minimum deal amount.',
        conditionPlaceholder: 'JSON condition (optional)',
        primaryValueHint: {
          role: 'Role key, e.g. HOS',
          user: 'User ID, e.g. 42',
          dealManager: 'Not needed — the deal manager will be used',
        },
      },
    },
  },
  vn: {
    common: { actions: { gotIt: 'Đã hiểu' } },
    routing: {
      requestTypeOptions: {
        commission_question: 'Câu hỏi về hoa hồng',
        meeting_request: 'Yêu cầu cuộc họp',
        strategy_question: 'Câu hỏi về chiến lược',
        other: 'Câu hỏi khác',
      },
      roleOptions: {
        HOS: 'Trưởng phòng kinh doanh (HOS)',
        ADMIN: 'Quản trị viên',
        MANAGER: 'Quản lý',
        AGENT: 'Đại lý',
        MARKETER: 'Nhân viên marketing',
      },
      emptyState: {
        title: 'Chưa có quy tắc định tuyến nào',
        description:
          'Quy tắc xác định yêu cầu của đại lý được chuyển đến ai (theo loại) và sau bao nhiêu phút thì leo thang nếu không ai phản hồi. Tạo quy tắc đầu tiên để yêu cầu tự động đến đúng người.',
        example: 'Ví dụ: «Câu hỏi về hoa hồng» → vai trò Trưởng phòng kinh doanh, leo thang sau 60 phút.',
      },
      howItWorks: {
        title: 'Cách hoạt động',
        intro:
          'Khi đại lý gửi yêu cầu, hệ thống chọn quy tắc theo loại yêu cầu và chuyển đến người phụ trách đã chọn. Nếu không có phản hồi trong thời gian SLA, yêu cầu sẽ leo thang đến vai trò dự phòng.',
        examplesLabel: 'Loại yêu cầu:',
        items: {
          requestType: {
            term: 'Loại yêu cầu',
            text: 'danh mục yêu cầu của đại lý: câu hỏi về hoa hồng, yêu cầu cuộc họp, câu hỏi về chiến lược hoặc khác.',
          },
          primary: {
            term: 'Chuyển đến',
            text: 'người phụ trách chính — một vai trò, một người dùng cụ thể hoặc quản lý giao dịch.',
          },
          fallback: {
            term: 'Vai trò dự phòng',
            text: 'người nhận yêu cầu nếu người phụ trách chính không phản hồi trong thời gian SLA. Mặc định là Trưởng phòng kinh doanh.',
          },
          sla: {
            term: 'SLA, phút',
            text: 'số phút được phép phản hồi trước khi leo thang đến vai trò dự phòng.',
          },
          condition: {
            term: 'Điều kiện',
            text: 'điều kiện JSON tùy chọn để quy tắc kích hoạt (ví dụ chỉ cho giao dịch giá trị cao).',
          },
        },
      },
      welcome: {
        title: 'Định tuyến yêu cầu — bắt đầu từ đâu',
        body: 'Tại đây bạn cấu hình yêu cầu của đại lý được tự động chuyển đến ai và sau bao nhiêu phút thì leo thang. Mỗi quy tắc liên kết một loại yêu cầu với người phụ trách và bộ đếm SLA. Xem bảng «Cách hoạt động» bên dưới để biết chi tiết từng trường.',
      },
      tooltips: {
        requestType: 'Danh mục yêu cầu. Chọn từ danh sách các loại đã biết.',
        primaryKind: 'Cách chọn người phụ trách: vai trò, người dùng cụ thể hoặc quản lý giao dịch.',
        primaryValue: 'Tùy theo lựa chọn: khóa vai trò, ID người dùng hoặc để trống cho quản lý giao dịch.',
        fallbackRoleKey: 'Vai trò mà yêu cầu sẽ leo thang khi quá hạn SLA.',
        slaMinutes: 'Thời gian đến khi leo thang, tính bằng phút.',
        condition: 'Điều kiện JSON tùy chọn để quy tắc kích hoạt.',
      },
      fields: {
        requestTypePlaceholder: 'Chọn loại yêu cầu',
        fallbackRoleKeyHint: 'Vai trò để leo thang khi quá hạn SLA (mặc định Trưởng phòng kinh doanh).',
        conditionHint:
          'Tùy chọn. Điều kiện JSON để quy tắc kích hoạt — ví dụ số tiền giao dịch tối thiểu.',
        conditionPlaceholder: 'Điều kiện JSON (tùy chọn)',
        primaryValueHint: {
          role: 'Khóa vai trò, ví dụ HOS',
          user: 'ID người dùng, ví dụ 42',
          dealManager: 'Không cần — sẽ dùng quản lý giao dịch',
        },
      },
    },
  },
}
